import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import "../../styles/AdminDashboard.css";
import "../../styles/Tables.css";

import {
  dispatchAdminOpsSelectedIncidentsApi,
  dispatchAdminOpsDigestApi,
  dispatchAdminOpsSlaAlertsApi,
  downloadAdminOpsAlertsAuditLogCsvApi,
  downloadAdminOpsAlertsCsvFilteredApi,
  escalateAdminOpsAcksApi,
  fetchAdminOpsAlertsAnalyticsApi,
  fetchAdminOpsAlertsFilteredApi,
  fetchAdminOpsAlertsAuditLogApi,
  fetchAdminOpsAlertsCorrelationsApi,
  fetchAdminOpsSlaPolicyApi,
  fetchAdminOpsAlertsSummaryApi,
  sendAdminOpsIncidentTestApi,
  sendAdminOpsDigestApi,
  setAdminOpsAlertAckApi,
  setAdminOpsAlertsBulkAckApi,
  setAdminOpsAlertsBulkOwnershipApi,
  setAdminOpsAlertOwnershipApi,
  updateAdminOpsSlaPolicyApi,
} from "../../lib/api";

function formatDate(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
}

function calcSlaState(item) {
  const rs = String(item?.response_status || "open").toLowerCase();
  if (rs === "closed") return "closed";
  const due = String(item?.sla_due_at || "").trim();
  if (!due) return "no_sla";
  const dt = new Date(due);
  if (Number.isNaN(dt.getTime())) return "invalid_sla";
  const deltaMs = dt.getTime() - Date.now();
  if (deltaMs < 0) return "overdue";
  if (deltaMs <= 24 * 60 * 60 * 1000) return "due_24h";
  return "on_track";
}

function summarizeVisible(items) {
  const list = Array.isArray(items) ? items : [];
  let acknowledged = 0;
  let overdueOpen = 0;
  let due24hOpen = 0;
  let withoutSlaOpen = 0;
  for (const it of list) {
    if (it?.acknowledged) acknowledged += 1;
    const st = calcSlaState(it);
    if (st === "overdue") overdueOpen += 1;
    if (st === "due_24h") due24hOpen += 1;
    if (st === "no_sla" || st === "invalid_sla") withoutSlaOpen += 1;
  }
  return {
    total: list.length,
    acknowledged,
    unacknowledged: Math.max(0, list.length - acknowledged),
    overdue_open: overdueOpen,
    due_24h_open: due24hOpen,
    without_sla_open: withoutSlaOpen,
  };
}

function formatSlaTimer(item) {
  const due = String(item?.sla_due_at || "").trim();
  if (!due) return "";
  const dt = new Date(due);
  if (Number.isNaN(dt.getTime())) return "";
  const deltaMs = dt.getTime() - Date.now();
  const absHours = Math.round(Math.abs(deltaMs) / (1000 * 60 * 60));
  if (deltaMs < 0) return `${absHours}h overdue`;
  return `${absHours}h left`;
}

export default function AdminOpsAlerts() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [dispatchingSla, setDispatchingSla] = useState(false);
  const [slaSendEmail, setSlaSendEmail] = useState(true);
  const [slaSendWebhook, setSlaSendWebhook] = useState(true);
  const [slaIncludeDueSoon, setSlaIncludeDueSoon] = useState(true);
  const [lastDispatch, setLastDispatch] = useState(null);
  const [actingAlertId, setActingAlertId] = useState("");
  const [editingAlertId, setEditingAlertId] = useState("");
  const [editDraft, setEditDraft] = useState({
    owner: "",
    sla_due_at: "",
    response_status: "open",
    resolution_note: "",
    triage_comment: "",
    change_reason: "",
  });
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResponseStatus, setBulkResponseStatus] = useState("in_progress");
  const [bulkOwner, setBulkOwner] = useState("");
  const [bulkSlaDueAt, setBulkSlaDueAt] = useState("");
  const [bulkResolutionNote, setBulkResolutionNote] = useState("");
  const [bulkTriageComment, setBulkTriageComment] = useState("");
  const [bulkChangeReason, setBulkChangeReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [ackFilter, setAckFilter] = useState("");
  const [responseStatusFilter, setResponseStatusFilter] = useState("");
  const [savedFilterPreset, setSavedFilterPreset] = useState(() => localStorage.getItem("ops_alerts_saved_preset") || "");
  const [myQueueOwner, setMyQueueOwner] = useState(() => localStorage.getItem("ops_alerts_my_owner") || "");
  const [payload, setPayload] = useState({ count: 0, items: [] });
  const [summaryPayload, setSummaryPayload] = useState({
    total: 0,
    acknowledged: 0,
    unacknowledged: 0,
    overdue_open: 0,
    due_24h_open: 0,
    without_sla_open: 0,
  });
  const [rowActionById, setRowActionById] = useState({});
  const [rowActionResultById, setRowActionResultById] = useState({});
  const [auditLogItems, setAuditLogItems] = useState([]);
  const [exportingAudit, setExportingAudit] = useState(false);
  const [analytics, setAnalytics] = useState({ summary: {}, per_day: [], owner_backlog: [] });
  const [correlations, setCorrelations] = useState([]);
  const [escalatingAcks, setEscalatingAcks] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [dispatchingSelectedIncidents, setDispatchingSelectedIncidents] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [slaPolicy, setSlaPolicy] = useState({
    severity_targets_hours: { critical: 4, warning: 24, info: 72 },
  });

  const setRowActionResult = (id, ok, message) => {
    const key = String(id || "");
    if (!key) return;
    setRowActionResultById((prev) => ({
      ...prev,
      [key]: {
        ok: !!ok,
        message: String(message || ""),
        at: new Date().toISOString(),
      },
    }));
  };

  const handlePotentialVersionConflict = async (err, fallbackMessage) => {
    const msg = String(err?.message || fallbackMessage || "Request failed");
    if (String(msg).toLowerCase().includes("version conflict")) {
      toast.error("Version conflict detected. Data refreshed. Please review latest values and retry.");
      await load();
      return true;
    }
    return false;
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [data, summary, auditLog, analyticsPayload, corrPayload, policyPayload] = await Promise.all([
        fetchAdminOpsAlertsFilteredApi({
          status: statusFilter,
          severity: severityFilter,
          acknowledged: ackFilter,
          response_status: responseStatusFilter,
          limit: 300,
        }),
        fetchAdminOpsAlertsSummaryApi({
          status: statusFilter,
          severity: severityFilter,
          acknowledged: ackFilter,
          response_status: responseStatusFilter,
        }),
        fetchAdminOpsAlertsAuditLogApi({ limit: 20 }),
        fetchAdminOpsAlertsAnalyticsApi({ days: 30 }),
        fetchAdminOpsAlertsCorrelationsApi({ limit: 10 }),
        fetchAdminOpsSlaPolicyApi(),
      ]);
      const rawItems = Array.isArray(data?.items) ? data.items : [];
      let visibleItems = rawItems.slice();
      if (savedFilterPreset === "my_queue") {
        const mine = String(myQueueOwner || "").trim().toLowerCase();
        visibleItems = visibleItems.filter((it) => mine && String(it?.owner || "").trim().toLowerCase() === mine);
      } else if (savedFilterPreset === "overdue_owner") {
        visibleItems = visibleItems.filter(
          (it) => calcSlaState(it) === "overdue" && String(it?.owner || "").trim()
        );
      }
      setPayload({
        count: visibleItems.length,
        items: visibleItems,
      });
      const localSummary = summarizeVisible(visibleItems);
      setSummaryPayload({
        total: Number(localSummary.total || summary?.total || 0),
        acknowledged: Number(localSummary.acknowledged || summary?.acknowledged || 0),
        unacknowledged: Number(localSummary.unacknowledged || summary?.unacknowledged || 0),
        overdue_open: Number(localSummary.overdue_open || summary?.overdue_open || 0),
        due_24h_open: Number(localSummary.due_24h_open || summary?.due_24h_open || 0),
        without_sla_open: Number(localSummary.without_sla_open || summary?.without_sla_open || 0),
      });
      setAuditLogItems(Array.isArray(auditLog?.items) ? auditLog.items : []);
      setAnalytics(analyticsPayload || { summary: {}, per_day: [], owner_backlog: [] });
      setCorrelations(Array.isArray(corrPayload?.items) ? corrPayload.items : []);
      setSlaPolicy(policyPayload?.policy || { severity_targets_hours: { critical: 4, warning: 24, info: 72 } });
    } catch (err) {
      toast.error(err?.message || "Failed to load ops alerts");
      setPayload({ count: 0, items: [] });
      setSummaryPayload({
        total: 0,
        acknowledged: 0,
        unacknowledged: 0,
        overdue_open: 0,
        due_24h_open: 0,
        without_sla_open: 0,
      });
      setAuditLogItems([]);
      setAnalytics({ summary: {}, per_day: [], owner_backlog: [] });
      setCorrelations([]);
      setSlaPolicy({ severity_targets_hours: { critical: 4, warning: 24, info: 72 } });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, ackFilter, responseStatusFilter, savedFilterPreset, myQueueOwner]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const visible = new Set((payload.items || []).map((it) => String(it?.id || "")));
    setSelectedAlertIds((prev) => prev.filter((id) => visible.has(id)));
  }, [payload.items]);

  const slaState = calcSlaState;

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await downloadAdminOpsAlertsCsvFilteredApi({
        status: statusFilter,
        severity: severityFilter,
        acknowledged: ackFilter,
        response_status: responseStatusFilter,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ops_alerts.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.message || "Failed to export ops alerts");
    } finally {
      setExporting(false);
    }
  };

  const handleAuditExport = async () => {
    try {
      setExportingAudit(true);
      const blob = await downloadAdminOpsAlertsAuditLogCsvApi({});
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ops_alerts_audit_log.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.message || "Failed to export ops alerts audit log");
    } finally {
      setExportingAudit(false);
    }
  };

  const beginOwnershipEdit = (item) => {
    const id = String(item?.id || "");
    if (!id) return;
    setEditingAlertId(id);
    setEditDraft({
      owner: String(item?.owner || ""),
      sla_due_at: String(item?.sla_due_at || ""),
      response_status: String(item?.response_status || "open"),
      resolution_note: String(item?.resolution_note || ""),
      triage_comment: String(item?.triage_comment || ""),
      change_reason: String(item?.change_reason || ""),
    });
  };

  const cancelOwnershipEdit = () => {
    setEditingAlertId("");
    setEditDraft({
      owner: "",
      sla_due_at: "",
      response_status: "open",
      resolution_note: "",
      triage_comment: "",
      change_reason: "",
    });
  };

  const saveOwnershipEdit = async () => {
    const id = String(editingAlertId || "");
    if (!id) return;
    const response = String(editDraft.response_status || "").trim();
    if (!["open", "in_progress", "mitigated", "closed"].includes(response)) {
      toast.error("Response status must be open, in_progress, mitigated or closed");
      return;
    }
    try {
      setActingAlertId(id);
      if (["mitigated", "closed"].includes(response) && !String(editDraft.change_reason || "").trim()) {
        toast.error("change_reason is required for mitigated/closed status");
        setActingAlertId("");
        return;
      }
      await setAdminOpsAlertOwnershipApi(id, {
        owner: String(editDraft.owner || ""),
        sla_due_at: String(editDraft.sla_due_at || ""),
        response_status: response,
        resolution_note: String(editDraft.resolution_note || ""),
        triage_comment: String(editDraft.triage_comment || ""),
        change_reason: String(editDraft.change_reason || ""),
        expected_version: itemVersionById[id],
      });
      toast.success("Owner/SLA updated");
      setRowActionResult(id, true, "Updated owner/SLA");
      cancelOwnershipEdit();
      await load();
    } catch (err) {
      if (await handlePotentialVersionConflict(err, "Owner/SLA update failed")) {
        setRowActionResult(id, false, err?.message || "Version conflict");
        return;
      }
      setRowActionResult(id, false, err?.message || "Owner/SLA update failed");
      toast.error(err?.message || "Failed to update owner/SLA");
    } finally {
      setActingAlertId("");
    }
  };

  const handleAckToggle = async (item, acknowledge) => {
    const id = String(item?.id || "");
    if (!id) return;
    const note = acknowledge ? window.prompt("Optional acknowledgment note", String(item?.ack_note || "")) || "" : "";
    const triageComment = window.prompt("Optional triage comment", String(item?.triage_comment || "")) || "";
    const changeReason = window.prompt("Optional change reason", String(item?.change_reason || "")) || "";
    try {
      setActingAlertId(id);
      await setAdminOpsAlertAckApi(id, {
        acknowledged: acknowledge,
        note,
        triage_comment: triageComment,
        change_reason: changeReason,
        expected_version: itemVersionById[id],
      });
      toast.success(acknowledge ? "Alert acknowledged" : "Alert unacknowledged");
      setRowActionResult(id, true, acknowledge ? "Acknowledged" : "Unacknowledged");
      await load();
    } catch (err) {
      if (await handlePotentialVersionConflict(err, "Ack update failed")) {
        setRowActionResult(id, false, err?.message || "Version conflict");
        return;
      }
      setRowActionResult(id, false, err?.message || "Ack update failed");
      toast.error(err?.message || "Failed to update alert acknowledgment");
    } finally {
      setActingAlertId("");
    }
  };

  const handleQuickResponseStatus = async (item, status) => {
    const id = String(item?.id || "");
    const nextStatus = String(status || "").trim();
    if (!id) return;
    if (!["open", "in_progress", "mitigated", "closed"].includes(nextStatus)) return;
    let changeReason = String(item?.change_reason || "");
    if (["mitigated", "closed"].includes(nextStatus)) {
      changeReason = window.prompt("Change reason (required for mitigated/closed)", changeReason) || "";
      if (!String(changeReason || "").trim()) {
        toast.error("change_reason is required for mitigated/closed status");
        return;
      }
    }
    try {
      setActingAlertId(id);
      await setAdminOpsAlertOwnershipApi(id, {
        response_status: nextStatus,
        change_reason: changeReason,
        expected_version: itemVersionById[id],
      });
      toast.success(`Set status to ${nextStatus}`);
      setRowActionResult(id, true, `Status set to ${nextStatus}`);
      await load();
    } catch (err) {
      if (await handlePotentialVersionConflict(err, "Status update failed")) {
        setRowActionResult(id, false, err?.message || "Version conflict");
        return;
      }
      setRowActionResult(id, false, err?.message || "Status update failed");
      toast.error(err?.message || "Failed to update response status");
    } finally {
      setActingAlertId("");
    }
  };

  const runRowAction = async (item) => {
    const id = String(item?.id || "");
    const action = String((rowActionById || {})[id] || "").trim();
    if (!id || !action) return;
    if (action === "ack") {
      await handleAckToggle(item, true);
      setRowActionById((prev) => ({ ...prev, [id]: "" }));
      return;
    }
    if (action === "unack") {
      await handleAckToggle(item, false);
      setRowActionById((prev) => ({ ...prev, [id]: "" }));
      return;
    }
    if (action === "edit") {
      beginOwnershipEdit(item);
      setRowActionById((prev) => ({ ...prev, [id]: "" }));
      return;
    }
    if (action.startsWith("status:")) {
      const status = action.split(":", 2)[1] || "";
      await handleQuickResponseStatus(item, status);
      setRowActionById((prev) => ({ ...prev, [id]: "" }));
    }
  };

  const handleIncidentTest = async () => {
    try {
      setSendingTest(true);
      const res = await sendAdminOpsIncidentTestApi();
      if (res?.delivered) toast.success("Incident test delivered");
      else toast.error(res?.message || "Incident test not delivered");
    } catch (err) {
      toast.error(err?.message || "Failed to send incident test");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSlaDispatch = async () => {
    try {
      setDispatchingSla(true);
      const res = await dispatchAdminOpsSlaAlertsApi({
        send_email: slaSendEmail,
        send_incident_webhook: slaSendWebhook,
        include_due_soon: slaIncludeDueSoon,
      });
      setLastDispatch({
        ran_at: new Date().toISOString(),
        ...res,
      });
      toast.success(
        `SLA dispatch: overdue=${Number(res?.overdue_count || 0)}, due_soon=${Number(res?.due_soon_count || 0)}, webhook_sent=${Number(res?.incident_webhook_sent || 0)}`
      );
      await load();
    } catch (err) {
      toast.error(err?.message || "Failed to dispatch SLA alerts");
    } finally {
      setDispatchingSla(false);
    }
  };

  const visibleIds = (payload.items || []).map((it) => String(it?.id || "")).filter(Boolean);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedAlertIds.includes(id));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedAlertIds([]);
      return;
    }
    setSelectedAlertIds(visibleIds);
  };

  const toggleSelectOne = (id) => {
    const key = String(id || "");
    if (!key) return;
    setSelectedAlertIds((prev) => {
      if (prev.includes(key)) return prev.filter((x) => x !== key);
      return [...prev, key];
    });
  };

  const runBulkAck = async (acknowledged) => {
    const ids = selectedAlertIds.slice();
    if (!ids.length) {
      toast.error("Select at least one alert");
      return;
    }
    try {
      setBulkBusy(true);
      const triageComment = window.prompt("Optional bulk triage comment", "") || "";
      const changeReason = window.prompt("Optional bulk change reason", "") || "";
      const res = await setAdminOpsAlertsBulkAckApi({
        alert_ids: ids,
        acknowledged,
        note: "",
        triage_comment: triageComment,
        change_reason: changeReason,
        expected_versions: ids.reduce((acc, id) => ({ ...acc, [id]: itemVersionById[id] }), {}),
      });
      toast.success(
        `Updated ${Number(res?.updated_count || 0)} alert(s)` +
        (Number(res?.not_found_count || 0) > 0 ? `, ${Number(res?.not_found_count || 0)} not found` : "") +
        (Number(res?.conflict_count || 0) > 0 ? `, ${Number(res?.conflict_count || 0)} conflict` : "")
      );
      setSelectedAlertIds([]);
      await load();
    } catch (err) {
      if (await handlePotentialVersionConflict(err, "Bulk ack update failed")) return;
      toast.error(err?.message || "Failed bulk ack update");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkResponseStatus = async () => {
    const ids = selectedAlertIds.slice();
    const status = String(bulkResponseStatus || "").trim();
    if (!ids.length) {
      toast.error("Select at least one alert");
      return;
    }
    if (!["open", "in_progress", "mitigated", "closed"].includes(status)) {
      toast.error("Choose a valid response status");
      return;
    }
    const reason = String(bulkChangeReason || "").trim();
    if (["mitigated", "closed"].includes(status) && !reason) {
      toast.error("change_reason is required for mitigated/closed status");
      return;
    }
    try {
      setBulkBusy(true);
      const res = await setAdminOpsAlertsBulkOwnershipApi({
        alert_ids: ids,
        response_status: status,
        change_reason: reason,
        expected_versions: ids.reduce((acc, id) => ({ ...acc, [id]: itemVersionById[id] }), {}),
      });
      toast.success(
        `Updated status for ${Number(res?.updated_count || 0)} alert(s)` +
        (Number(res?.not_found_count || 0) > 0 ? `, ${Number(res?.not_found_count || 0)} not found` : "") +
        (Number(res?.conflict_count || 0) > 0 ? `, ${Number(res?.conflict_count || 0)} conflict` : "")
      );
      setSelectedAlertIds([]);
      await load();
    } catch (err) {
      if (await handlePotentialVersionConflict(err, "Bulk response status update failed")) return;
      toast.error(err?.message || "Failed bulk response status update");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkAssignOwnerSla = async () => {
    const ids = selectedAlertIds.slice();
    const owner = String(bulkOwner || "").trim();
    const sla_due_at = String(bulkSlaDueAt || "").trim();
    const resolution_note = String(bulkResolutionNote || "").trim();
    const triage_comment = String(bulkTriageComment || "").trim();
    const change_reason = String(bulkChangeReason || "").trim();
    if (!ids.length) {
      toast.error("Select at least one alert");
      return;
    }
    if (!owner && !sla_due_at && !resolution_note && !triage_comment && !change_reason) {
      toast.error("Enter at least one field before bulk update");
      return;
    }
    try {
      setBulkBusy(true);
      const payload = { alert_ids: ids };
      if (owner) payload.owner = owner;
      if (sla_due_at) payload.sla_due_at = sla_due_at;
      if (resolution_note) payload.resolution_note = resolution_note;
      if (triage_comment) payload.triage_comment = triage_comment;
      if (change_reason) payload.change_reason = change_reason;
      payload.expected_versions = ids.reduce((acc, id) => ({ ...acc, [id]: itemVersionById[id] }), {});
      const res = await setAdminOpsAlertsBulkOwnershipApi(payload);
      toast.success(
        `Updated ${Number(res?.updated_count || 0)} alert(s)` +
        (Number(res?.not_found_count || 0) > 0 ? `, ${Number(res?.not_found_count || 0)} not found` : "") +
        (Number(res?.conflict_count || 0) > 0 ? `, ${Number(res?.conflict_count || 0)} conflict` : "")
      );
      setSelectedAlertIds([]);
      await load();
    } catch (err) {
      if (await handlePotentialVersionConflict(err, "Bulk owner/SLA update failed")) return;
      toast.error(err?.message || "Failed bulk owner/SLA update");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleEscalateAcks = async () => {
    try {
      setEscalatingAcks(true);
      const res = await escalateAdminOpsAcksApi({ send_incident_webhook: true });
      toast.success(`Escalated ${Number(res?.escalated_count || 0)} stale acknowledged alert(s)`);
      await load();
    } catch (err) {
      toast.error(err?.message || "Failed to escalate stale acknowledgements");
    } finally {
      setEscalatingAcks(false);
    }
  };

  const handleSendDigest = async () => {
    try {
      setSendingDigest(true);
      const res = await sendAdminOpsDigestApi({ days: 7, include_audit_tail: 20 });
      toast.success(`Digest sent to ${Number(res?.recipient_count || 0)} recipient(s)`);
      await load();
    } catch (err) {
      toast.error(err?.message || "Failed to send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const handleDispatchDigest = async () => {
    try {
      setSendingDigest(true);
      const res = await dispatchAdminOpsDigestApi({ force: false });
      toast.success(String(res?.message || "Digest dispatch completed"));
      await load();
    } catch (err) {
      toast.error(err?.message || "Failed to dispatch digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const runBulkIncidentDispatch = async () => {
    const ids = selectedAlertIds.slice();
    if (!ids.length) {
      toast.error("Select at least one alert");
      return;
    }
    const routingKey = window.prompt("Optional incident routing key (team/service queue)", "") || "";
    try {
      setDispatchingSelectedIncidents(true);
      const res = await dispatchAdminOpsSelectedIncidentsApi({
        alert_ids: ids,
        routing_key: routingKey,
        send_only_open: true,
      });
      toast.success(
        `Incident dispatch: routed=${Number(res?.routed_count || 0)}, failed=${Number(res?.failed_count || 0)}, skipped=${Number(res?.skipped_count || 0)}`
      );
      await load();
    } catch (err) {
      toast.error(err?.message || "Failed to dispatch selected incidents");
    } finally {
      setDispatchingSelectedIncidents(false);
    }
  };

  const handleSaveSlaPolicy = async () => {
    try {
      setSavingPolicy(true);
      const t = slaPolicy?.severity_targets_hours || {};
      await updateAdminOpsSlaPolicyApi({
        severity_targets_hours: {
          critical: Number(t.critical || 4),
          warning: Number(t.warning || 24),
          info: Number(t.info || 72),
        },
      });
      toast.success("SLA policy updated");
      await load();
    } catch (err) {
      toast.error(err?.message || "Failed to save SLA policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  const itemVersionById = (payload.items || []).reduce((acc, it) => {
    const id = String(it?.id || "");
    if (id) acc[id] = Number(it?.version || 1);
    return acc;
  }, {});

  return (
    <div className="admin-page">
      <div className="admin-hero">
        <div>
          <h1>Ops Alerts</h1>
          <p className="subtitle">Live alert history from Alertmanager webhook ingestion.</p>
        </div>
        <div className="admin-hero-actions" style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary" onClick={handleEscalateAcks} disabled={escalatingAcks}>
            {escalatingAcks ? "Escalating..." : "Escalate Stale Acks"}
          </button>
          <button className="btn-secondary" onClick={handleSendDigest} disabled={sendingDigest}>
            {sendingDigest ? "Sending..." : "Send Digest"}
          </button>
          <button className="btn-secondary" onClick={handleDispatchDigest} disabled={sendingDigest}>
            {sendingDigest ? "Dispatching..." : "Dispatch Digest"}
          </button>
          <button className="btn-secondary" onClick={handleSlaDispatch} disabled={dispatchingSla}>
            {dispatchingSla ? "Dispatching..." : "Dispatch SLA Alerts"}
          </button>
          <button className="btn-secondary" onClick={handleIncidentTest} disabled={sendingTest}>
            {sendingTest ? "Sending..." : "Send Incident Test"}
          </button>
          <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="admin-kpis">
        <div className="card kpi">
          <div className="kpi-label">Visible alerts</div>
          <div className="kpi-value">{Number(summaryPayload.total || 0)}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Overdue SLA</div>
          <div className="kpi-value">{summaryPayload.overdue_open}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Due in 24h</div>
          <div className="kpi-value">{summaryPayload.due_24h_open}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">No SLA</div>
          <div className="kpi-value">{summaryPayload.without_sla_open}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Avg Ack (s)</div>
          <div className="kpi-value">{analytics?.summary?.avg_time_to_ack_seconds ?? "-"}</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Avg Close (s)</div>
          <div className="kpi-value">{analytics?.summary?.avg_time_to_close_seconds ?? "-"}</div>
        </div>
      </div>

      <div className="admin-card admin-section">
        <div style={{ marginBottom: 12, padding: 10, border: "1px solid rgba(15,23,42,0.12)", borderRadius: 8 }}>
          <strong>SLA Policy</strong>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "center", fontSize: 12 }}>
            <label>
              critical (h)
              <input
                value={String((slaPolicy?.severity_targets_hours || {}).critical || "")}
                onChange={(e) => setSlaPolicy((prev) => ({
                  ...prev,
                  severity_targets_hours: {
                    ...(prev?.severity_targets_hours || {}),
                    critical: e.target.value,
                  },
                }))}
                style={{ marginLeft: 6, width: 72 }}
              />
            </label>
            <label>
              warning (h)
              <input
                value={String((slaPolicy?.severity_targets_hours || {}).warning || "")}
                onChange={(e) => setSlaPolicy((prev) => ({
                  ...prev,
                  severity_targets_hours: {
                    ...(prev?.severity_targets_hours || {}),
                    warning: e.target.value,
                  },
                }))}
                style={{ marginLeft: 6, width: 72 }}
              />
            </label>
            <label>
              info (h)
              <input
                value={String((slaPolicy?.severity_targets_hours || {}).info || "")}
                onChange={(e) => setSlaPolicy((prev) => ({
                  ...prev,
                  severity_targets_hours: {
                    ...(prev?.severity_targets_hours || {}),
                    info: e.target.value,
                  },
                }))}
                style={{ marginLeft: 6, width: 72 }}
              />
            </label>
            <button className="btn-secondary" onClick={handleSaveSlaPolicy} disabled={savingPolicy}>
              {savingPolicy ? "Saving..." : "Save Policy"}
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 12, padding: 10, border: "1px solid rgba(15,23,42,0.12)", borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <strong>Recent Alert Changes</strong>
            <button className="btn-secondary" onClick={handleAuditExport} disabled={exportingAudit}>
              {exportingAudit ? "Exporting..." : "Export Change Log CSV"}
            </button>
          </div>
          <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto", fontSize: 12 }}>
            {auditLogItems.length === 0 ? (
              <div style={{ opacity: 0.75 }}>No recent changes</div>
            ) : (
              auditLogItems.map((ev) => (
                <div key={String(ev.id || `${ev.timestamp}-${ev.action}`)} style={{ padding: "4px 0", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
                  <strong>{String(ev.action || "-")}</strong> | alert={String(ev.alert_id || "-")} | actor={String(ev.actor || "-")} | {formatDate(ev.timestamp)}
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ marginBottom: 12, padding: 10, border: "1px solid rgba(15,23,42,0.12)", borderRadius: 8 }}>
          <strong>Top Correlations</strong>
          <div style={{ marginTop: 8, maxHeight: 160, overflowY: "auto", fontSize: 12 }}>
            {(correlations || []).length === 0 ? (
              <div style={{ opacity: 0.75 }}>No correlation groups</div>
            ) : (
              (correlations || []).map((c) => (
                <div key={String(c.correlation_key || `${c.service}-${c.alertname}`)} style={{ padding: "3px 0" }}>
                  {String(c.service || "-")} / {String(c.alertname || "-")} | open={Number(c.open_count || 0)} total={Number(c.count || 0)}
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ marginBottom: 12, padding: 10, border: "1px solid rgba(15,23,42,0.12)", borderRadius: 8 }}>
          <strong>Alerts Per Day (30d)</strong>
          <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto", fontSize: 12 }}>
            {(analytics?.per_day || []).length === 0 ? (
              <div style={{ opacity: 0.75 }}>No analytics data</div>
            ) : (
              (analytics.per_day || []).slice(-14).map((r) => {
                const max = Math.max(1, ...(analytics.per_day || []).map((x) => Number(x?.alerts || 0)));
                const w = Math.round((Number(r?.alerts || 0) / max) * 100);
                return (
                  <div key={String(r?.date || Math.random())} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ width: 90 }}>{String(r?.date || "-")}</span>
                    <div style={{ flex: 1, background: "rgba(15,23,42,0.08)", borderRadius: 4, overflow: "hidden", height: 10 }}>
                      <div style={{ width: `${w}%`, height: "100%", background: "rgba(22,163,74,0.65)" }} />
                    </div>
                    <span style={{ width: 36, textAlign: "right" }}>{Number(r?.alerts || 0)}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div style={{ marginBottom: 12, padding: 10, border: "1px solid rgba(15,23,42,0.12)", borderRadius: 8 }}>
          <strong>Owner Backlog</strong>
          <div style={{ marginTop: 8, maxHeight: 160, overflowY: "auto", fontSize: 12 }}>
            {(analytics?.owner_backlog || []).length === 0 ? (
              <div style={{ opacity: 0.75 }}>No owner backlog</div>
            ) : (
              (analytics.owner_backlog || []).slice(0, 10).map((r) => (
                <div key={String(r?.owner || Math.random())} style={{ padding: "3px 0" }}>
                  {String(r?.owner || "unassigned")}: {Number(r?.open_alerts || 0)}
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <button
            className="btn-secondary"
            onClick={() => {
              setSavedFilterPreset("");
              localStorage.setItem("ops_alerts_saved_preset", "");
            }}
          >
            All Alerts
          </button>
          <button
            className="btn-secondary"
            onClick={() => {
              setSavedFilterPreset("my_queue");
              localStorage.setItem("ops_alerts_saved_preset", "my_queue");
            }}
          >
            My Queue
          </button>
          <button
            className="btn-secondary"
            onClick={() => {
              setSavedFilterPreset("overdue_owner");
              localStorage.setItem("ops_alerts_saved_preset", "overdue_owner");
            }}
          >
            Overdue By Owner
          </button>
          <input
            value={myQueueOwner}
            onChange={(e) => {
              setMyQueueOwner(e.target.value);
              localStorage.setItem("ops_alerts_my_owner", e.target.value);
            }}
            placeholder="My queue owner name"
            style={{ minWidth: 180 }}
          />
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={slaSendEmail}
              onChange={(e) => setSlaSendEmail(!!e.target.checked)}
            />
            SLA dispatch email
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={slaSendWebhook}
              onChange={(e) => setSlaSendWebhook(!!e.target.checked)}
            />
            SLA dispatch webhook
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={slaIncludeDueSoon}
              onChange={(e) => setSlaIncludeDueSoon(!!e.target.checked)}
            />
            Include due-soon alerts
          </label>
        </div>

        {lastDispatch ? (
          <div
            style={{
              marginBottom: 12,
              padding: 10,
              border: "1px solid rgba(15,23,42,0.12)",
              borderRadius: 8,
              background: "rgba(15,23,42,0.02)",
              fontSize: 13,
            }}
          >
            <strong>Last SLA dispatch</strong>
            <div>Ran: {formatDate(lastDispatch.ran_at)}</div>
            <div>
              Overdue: {Number(lastDispatch.overdue_count || 0)} | Due soon: {Number(lastDispatch.due_soon_count || 0)}
            </div>
            <div>
              Email: {lastDispatch.email_sent ? "sent" : "not sent"}
              {lastDispatch.email_message ? ` (${lastDispatch.email_message})` : ""}
            </div>
            <div>
              Incident webhook: sent={Number(lastDispatch.incident_webhook_sent || 0)} failed={Number(lastDispatch.incident_webhook_failed || 0)}
            </div>
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="firing">firing</option>
            <option value="resolved">resolved</option>
          </select>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
            <option value="">All severities</option>
            <option value="critical">critical</option>
            <option value="warning">warning</option>
            <option value="info">info</option>
          </select>
          <select value={ackFilter} onChange={(e) => setAckFilter(e.target.value)}>
            <option value="">Ack any</option>
            <option value="true">acknowledged</option>
            <option value="false">unacknowledged</option>
          </select>
          <select value={responseStatusFilter} onChange={(e) => setResponseStatusFilter(e.target.value)}>
            <option value="">Response any</option>
            <option value="open">open</option>
            <option value="in_progress">in_progress</option>
            <option value="mitigated">mitigated</option>
            <option value="closed">closed</option>
          </select>
          <button className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 13, opacity: 0.85 }}>Selected: {selectedAlertIds.length}</span>
          <button className="btn-secondary" onClick={() => runBulkAck(true)} disabled={bulkBusy || selectedAlertIds.length === 0}>
            {bulkBusy ? "Working..." : "Bulk Acknowledge"}
          </button>
          <button className="btn-secondary" onClick={() => runBulkAck(false)} disabled={bulkBusy || selectedAlertIds.length === 0}>
            {bulkBusy ? "Working..." : "Bulk Unack"}
          </button>
          <select value={bulkResponseStatus} onChange={(e) => setBulkResponseStatus(e.target.value)} disabled={bulkBusy}>
            <option value="open">open</option>
            <option value="in_progress">in_progress</option>
            <option value="mitigated">mitigated</option>
            <option value="closed">closed</option>
          </select>
          <button className="btn-secondary" onClick={runBulkResponseStatus} disabled={bulkBusy || selectedAlertIds.length === 0}>
            {bulkBusy ? "Working..." : "Bulk Set Status"}
          </button>
          <button className="btn-secondary" onClick={() => setSelectedAlertIds([])} disabled={bulkBusy || selectedAlertIds.length === 0}>
            Clear Selection
          </button>
          <button
            className="btn-secondary"
            onClick={runBulkIncidentDispatch}
            disabled={dispatchingSelectedIncidents || selectedAlertIds.length === 0}
          >
            {dispatchingSelectedIncidents ? "Dispatching..." : "Dispatch Selected Incidents"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <input
            value={bulkOwner}
            onChange={(e) => setBulkOwner(e.target.value)}
            placeholder="Bulk owner"
            disabled={bulkBusy}
          />
          <input
            value={bulkSlaDueAt}
            onChange={(e) => setBulkSlaDueAt(e.target.value)}
            placeholder="Bulk SLA due (ISO date/datetime)"
            disabled={bulkBusy}
          />
          <input
            value={bulkResolutionNote}
            onChange={(e) => setBulkResolutionNote(e.target.value)}
            placeholder="Bulk resolution note"
            disabled={bulkBusy}
          />
          <input
            value={bulkTriageComment}
            onChange={(e) => setBulkTriageComment(e.target.value)}
            placeholder="Bulk triage comment"
            disabled={bulkBusy}
          />
          <input
            value={bulkChangeReason}
            onChange={(e) => setBulkChangeReason(e.target.value)}
            placeholder="Bulk change reason"
            disabled={bulkBusy}
          />
          <button className="btn-secondary" onClick={runBulkAssignOwnerSla} disabled={bulkBusy || selectedAlertIds.length === 0}>
            {bulkBusy ? "Working..." : "Bulk Assign Owner/SLA"}
          </button>
        </div>

        {editingAlertId ? (
          <div
            style={{
              marginBottom: 12,
              padding: 12,
              border: "1px solid rgba(15,23,42,0.12)",
              borderRadius: 8,
              background: "rgba(15,23,42,0.02)",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Edit Alert Ownership / SLA</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(220px, 1fr))", gap: 8 }}>
              <input
                value={editDraft.owner}
                onChange={(e) => setEditDraft((prev) => ({ ...prev, owner: e.target.value }))}
                placeholder="Owner"
              />
              <input
                value={editDraft.sla_due_at}
                onChange={(e) => setEditDraft((prev) => ({ ...prev, sla_due_at: e.target.value }))}
                placeholder="SLA due (ISO date/datetime)"
              />
              <select
                value={editDraft.response_status}
                onChange={(e) => setEditDraft((prev) => ({ ...prev, response_status: e.target.value }))}
              >
                <option value="open">open</option>
                <option value="in_progress">in_progress</option>
                <option value="mitigated">mitigated</option>
                <option value="closed">closed</option>
              </select>
              <input
                value={editDraft.resolution_note}
                onChange={(e) => setEditDraft((prev) => ({ ...prev, resolution_note: e.target.value }))}
                placeholder="Resolution note"
              />
              <input
                value={editDraft.triage_comment}
                onChange={(e) => setEditDraft((prev) => ({ ...prev, triage_comment: e.target.value }))}
                placeholder="Triage comment"
              />
              <input
                value={editDraft.change_reason}
                onChange={(e) => setEditDraft((prev) => ({ ...prev, change_reason: e.target.value }))}
                placeholder="Change reason"
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="btn-secondary" disabled={actingAlertId === editingAlertId} onClick={saveOwnershipEdit}>
                {actingAlertId === editingAlertId ? "Saving..." : "Save"}
              </button>
              <button className="btn-secondary" disabled={actingAlertId === editingAlertId} onClick={cancelOwnershipEdit}>
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Select all visible alerts"
                  />
                </th>
                <th>Received</th>
                <th>Alert</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Service</th>
                <th>Summary</th>
                <th>Owner</th>
                <th>SLA Due</th>
                <th>Response</th>
                <th>Incident</th>
                <th>Ack</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13}>Loading...</td>
                </tr>
              ) : (payload.items || []).length === 0 ? (
                <tr>
                  <td colSpan={13}>No alerts found.</td>
                </tr>
              ) : (
                (payload.items || []).map((it) => (
                  <tr
                    key={String(it.id || `${it.fingerprint}-${it.received_at}`)}
                    style={
                      slaState(it) === "overdue"
                        ? { backgroundColor: "rgba(220, 38, 38, 0.08)" }
                        : slaState(it) === "due_24h"
                          ? { backgroundColor: "rgba(245, 158, 11, 0.08)" }
                          : undefined
                    }
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedAlertIds.includes(String(it.id || ""))}
                        onChange={() => toggleSelectOne(it.id)}
                        aria-label={`Select alert ${String(it.alertname || it.id || "")}`}
                      />
                    </td>
                    <td>{formatDate(it.received_at)}</td>
                    <td>{it.alertname || "-"}</td>
                    <td>{it.severity || "-"}</td>
                    <td>{it.alert_status || "-"}</td>
                    <td>{it.service || "-"}</td>
                    <td>{it.summary || "-"}</td>
                    <td>{it.owner || "-"}</td>
                    <td>
                      {it.sla_due_at || "-"}
                      {" "}
                      <small style={{ opacity: 0.7 }}>
                        ({slaState(it)})
                      </small>
                      {it.sla_due_at ? (
                        <>
                          {" "}
                          <small style={{ opacity: 0.7 }}>
                            {formatSlaTimer(it)}
                          </small>
                        </>
                      ) : null}
                    </td>
                    <td>{it.response_status || "open"}</td>
                    <td>{it.incident_dispatched ? "sent" : "not sent"}</td>
                    <td>
                      {it.acknowledged ? `yes${it.acknowledged_by ? ` (${it.acknowledged_by})` : ""}` : "no"}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 210 }}>
                        <select
                          value={String((rowActionById || {})[String(it.id || "")] || "")}
                          onChange={(e) => setRowActionById((prev) => ({ ...prev, [String(it.id || "")]: e.target.value }))}
                          disabled={actingAlertId === it.id}
                        >
                          <option value="">Select action</option>
                          <option value={it.acknowledged ? "unack" : "ack"}>
                            {it.acknowledged ? "Unacknowledge" : "Acknowledge"}
                          </option>
                          <option value="edit">{editingAlertId === it.id ? "Edit (open)" : "Edit details"}</option>
                          <option value="status:in_progress">Set: in_progress</option>
                          <option value="status:mitigated">Set: mitigated</option>
                          <option value="status:closed">Set: closed</option>
                        </select>
                        <button
                          className="btn-secondary"
                          disabled={actingAlertId === it.id || !String((rowActionById || {})[String(it.id || "")] || "").trim()}
                          onClick={() => runRowAction(it)}
                        >
                          {actingAlertId === it.id ? "Saving..." : "Apply"}
                        </button>
                      </div>
                      {rowActionResultById[String(it.id || "")] ? (
                        <div
                          style={{
                            marginTop: 6,
                            display: "inline-block",
                            fontSize: 11,
                            padding: "3px 7px",
                            borderRadius: 999,
                            border: "1px solid",
                            borderColor: rowActionResultById[String(it.id || "")].ok
                              ? "rgba(22, 163, 74, 0.28)"
                              : "rgba(220, 38, 38, 0.28)",
                            background: rowActionResultById[String(it.id || "")].ok
                              ? "rgba(22, 163, 74, 0.10)"
                              : "rgba(220, 38, 38, 0.10)",
                          }}
                          title={String(rowActionResultById[String(it.id || "")].message || "")}
                        >
                          {rowActionResultById[String(it.id || "")].ok ? "OK" : "ERR"}{" "}
                          {formatDate(rowActionResultById[String(it.id || "")].at)}
                        </div>
                      ) : null}
                      <div style={{ marginTop: 6, fontSize: 11, opacity: 0.75 }}>
                        {it.updated_by ? `Updated by ${it.updated_by}` : "Not updated"}{" "}
                        {it.updated_at ? `at ${formatDate(it.updated_at)}` : ""}
                        {it.change_reason ? ` | reason: ${it.change_reason}` : ""}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
